/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { Field, FormikProps } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, FormControl, FormHelperText, InputLabel } from '@mui/material';

import { OutlinedPasswordInput } from '../../../../elements/OutlinedPasswordInput/OutlinedPasswordInput';
import { Namespaces } from '../../../../i18n';
import { useFormStyles } from '../../../../styles/form';
import { useChangePasswordFormStyles } from './ChangePassword.styles';
import { ChangePasswordFormValues, ResetApiErrorForField, SignUpPageApiValidationErrors } from './ChangePassword.types';

type ChangePasswordFormProps = FormikProps<ChangePasswordFormValues> & {
  apiErrors: SignUpPageApiValidationErrors;
  resetApiError: ResetApiErrorForField;
  isFetching?: boolean;
  errorMessage?: string;
};

export function ChangePasswordForm(props: ChangePasswordFormProps) {
  const {
    errors,
    values,
    handleChange,
    touched,
    apiErrors,
    resetApiError,
    handleBlur,
    handleSubmit,
    resetForm,
    isValidating,
    isFetching,
    errorMessage,
    dirty,
  } = props;
  const classes = useChangePasswordFormStyles();
  const formClasses = useFormStyles();
  const { t } = useTranslation(Namespaces.UserProfile);

  const hasError = (name: keyof ChangePasswordFormValues): boolean | undefined => {
    return touched[name] && !!(apiErrors[name] || errors[name]);
  };
  const getError = (name: keyof ChangePasswordFormValues): string | undefined => {
    const error = errors[name] || apiErrors[name];
    if (error) {
      return t(`profile.validation.${name}.${error}`);
    }
    return undefined;
  };

  const onChange = (name: keyof SignUpPageApiValidationErrors, event: React.ChangeEvent) => {
    handleChange(event);
    resetApiError(name);
  };

  return (
    <form className={classes.form} onSubmit={handleSubmit}>
      <FormControl className={cx(formClasses.formControl)} error={hasError('oldPassword')} variant="outlined">
        <InputLabel htmlFor="oldPassword">{t('profile.label.current_password')}</InputLabel>
        <OutlinedPasswordInput
          id="oldPassword"
          name="oldPassword"
          value={values.oldPassword}
          onChange={onChange.bind(null, 'oldPassword')}
          label={t('profile.label.current_password')}
          fullWidth
          onBlur={handleBlur}
          disabled={isFetching}
        />
        {hasError('oldPassword') && <FormHelperText>{getError('oldPassword')}</FormHelperText>}
      </FormControl>
      <Field name="newPassword" type="text">
        {() => (
          <FormControl className={cx(formClasses.formControl)} error={hasError('newPassword')} variant="outlined">
            <InputLabel htmlFor="newPassword">{t('profile.label.new_password')}</InputLabel>
            <OutlinedPasswordInput
              id="newPassword"
              name="newPassword"
              value={values.newPassword}
              onChange={onChange.bind(null, 'newPassword')}
              label={t('profile.label.new_password')}
              fullWidth
              onBlur={handleBlur}
              disabled={isFetching}
            />
            {hasError('newPassword') && <FormHelperText>{getError('newPassword')}</FormHelperText>}
          </FormControl>
        )}
      </Field>
      <FormControl className={cx(formClasses.formControl)} error={hasError('newPasswordConfirm')} variant="outlined">
        <InputLabel htmlFor="newPasswordConfirm">{t('profile.label.verify_password')}</InputLabel>
        <OutlinedPasswordInput
          id="newPasswordConfirm"
          name="newPasswordConfirm"
          value={values.newPasswordConfirm}
          onChange={onChange.bind(null, 'newPasswordConfirm')}
          label={t('profile.label.verify_password')}
          disabled={isFetching}
          fullWidth
          onBlur={handleBlur}
        />
        {hasError('newPasswordConfirm') && <FormHelperText>{getError('newPasswordConfirm')}</FormHelperText>}
      </FormControl>
      {errorMessage && <div className={classes.errorBlock}>{t(`profile.error.${errorMessage}`)}</div>}
      <div className={classes.buttons}>
        <div className={classes.buttonWrap}>
          <Button variant="contained" type="submit" disabled={isValidating || isFetching || !dirty}>
            {t('profile.buttons.submit_form')}
          </Button>
        </div>
        <div className={classes.buttonWrap}>
          <Button onClick={() => resetForm()} variant="outlined" disabled={isValidating || isFetching || !dirty}>
            {t('profile.buttons.cancel_form')}
          </Button>
        </div>
      </div>
    </form>
  );
}
